/*!
 * extensionLoader.js — Score-specific extension system for OscillaScore
 * -----------------------------------------------------------
 * Loads JavaScript extensions from scores/{project}/extensions/
 * Allows composers to extend Oscilla without modifying core codebase.
 *
 * Usage in score.svg:
 *   <g id="ext(fn:myExtension.onSelect, on:select)"/>
 *
 * Extension file (extensions/myExtension.js):
 *   export function onSelect(ctx) {
 *     const { selected, all, event } = ctx;
 *     // ... custom logic
 *   }
 *
 * An extension that leaves something behind (a canvas, a timer, a window
 * listener) exports cleanup(ctx): it is called when another project opens,
 * and its ext(on:load) runs again when this one is opened again.
 */

import { onReset } from "./teardown.js";

// Global registry of loaded extensions per project
window._oscillaExtensions = window._oscillaExtensions || {};

// Track which projects have been loaded
const loadedProjects = new Set();
// the project whose extensions were last asked for: the open one
let openProject = null;

/**
 * Load all extensions for a project
 * @param {string} projectName - Name of the project
 * @returns {Promise<Object>} - Map of extension modules
 */
export async function loadProjectExtensions(projectName) {
  openProject = projectName;
  if (loadedProjects.has(projectName)) {
    return window._oscillaExtensions[projectName] || {};
  }

  const extensionsPath = `scores/${projectName}/extensions/`;
  window._oscillaExtensions[projectName] = {};

  try {
    // Fetch list of extension files from server
    const res = await fetch(`/api/extensions/${encodeURIComponent(projectName)}`);

    if (!res.ok) {
      if (res.status !== 404) {
        console.warn(`[Extensions] Failed to list extensions: ${res.status}`);
      }
      loadedProjects.add(projectName);
      return {};
    }

    const files = await res.json();

    if (!files || files.length === 0) {
      loadedProjects.add(projectName);
      return {};
    }

    // Load each extension module
    for (const filename of files) {
      if (!filename.endsWith('.js')) continue;

      const moduleName = filename.replace('.js', '');
      const modulePath = `/${extensionsPath}${filename}`;

      try {
        const module = await import(modulePath);
        window._oscillaExtensions[projectName][moduleName] = module;
        console.log(`[Extensions] Loaded: ${moduleName}`, Object.keys(module));
      } catch (err) {
        console.error(`[Extensions] Failed to load ${moduleName}:`, err);
      }
    }

    loadedProjects.add(projectName);

    return window._oscillaExtensions[projectName];

  } catch (err) {
    console.error(`[Extensions] Error loading extensions for "${projectName}":`, err);
    loadedProjects.add(projectName);
    return {};
  }
}

/**
 * Get an extension function by path (e.g., "myExtension.onSelect")
 * @param {string} fnPath - Function path like "moduleName.functionName"
 * @returns {Function|null} - The function or null if not found
 */
export function getExtensionFunction(fnPath) {
  const projectName = window.currentProject || window.currentProjectName;
  if (!projectName) {
    console.warn('[Extensions] No project loaded');
    return null;
  }

  const projectExts = window._oscillaExtensions[projectName];
  if (!projectExts) {
    console.warn(`[Extensions] No extensions loaded for "${projectName}"`);
    return null;
  }

  const parts = fnPath.split('.');
  if (parts.length < 2) {
    console.warn(`[Extensions] Invalid function path: "${fnPath}" (expected "module.function")`);
    return null;
  }

  const [moduleName, ...fnParts] = parts;
  const fnName = fnParts.join('.');

  const module = projectExts[moduleName];
  if (!module) {
    console.warn(`[Extensions] Module not found: "${moduleName}"`);
    return null;
  }

  const fn = module[fnName];
  if (typeof fn !== 'function') {
    console.warn(`[Extensions] Function not found: "${fnPath}"`);
    return null;
  }

  return fn;
}

/**
 * Call an extension function with context
 * @param {string} fnPath - Function path like "moduleName.functionName"
 * @param {Object} context - Context object passed to the function
 * @returns {*} - Return value from the function
 */
export function callExtension(fnPath, context = {}) {
  const fn = getExtensionFunction(fnPath);
  if (!fn) return undefined;

  try {
    return fn(context);
  } catch (err) {
    console.error(`[Extensions] Error calling ${fnPath}:`, err);
    return undefined;
  }
}

// The open project's extensions, let go when another opens: each module's
// cleanup() export, if it has one. The modules stay loaded (the browser keeps
// an imported module), so coming back does not load them a second time.
onReset("project", function cleanupExtensions() {
  const project = openProject;
  openProject = null;
  for (const [name, module] of Object.entries(window._oscillaExtensions[project] || {})) {
    if (typeof module.cleanup !== "function") continue;
    try { module.cleanup({ project }); } catch (err) { console.error(`[Extensions] ${name}.cleanup failed:`, err); }
  }
});

/**
 * Reset extensions for a project (call on project unload)
 * @param {string} projectName - Name of the project
 */
export function unloadProjectExtensions(projectName) {
  if (window._oscillaExtensions[projectName]) {
    delete window._oscillaExtensions[projectName];
  }
  loadedProjects.delete(projectName);
}

// Expose to window for debugging
window.oscillaExtensions = {
  load: loadProjectExtensions,
  get: getExtensionFunction,
  call: callExtension,
  unload: unloadProjectExtensions,
  list: () => window._oscillaExtensions
};
